import { takeLatest, call, put, select } from "redux-saga/effects";
import { accessPointOptionsReader } from "screens/private/readers-tags/readers/components/types";
import YmsNotification from "utils/functions/notification";
import { translate } from "utils/intl/IntlProvider";
import { getError } from "utils/requests/api.errors";
import * as Api from "./readers-api";
import { Creators as CreatorsReaders, Types } from "./readers-duck";
import { IAntennaForm, IDetailsItems, IForm, IItems } from "./readers-model";

export const getReadersFromState = (state) => state.Readers;
export const getFilterFromStateUserSession = (state) => state.UserSession;

const REQUIRED_ANTENNA_FIELDS: (keyof IAntennaForm)[] = ["antennaNumber", "antennaPort", "antennaPositionType", "flow"];

export const isAntennaComplete = (antenna?: IAntennaForm) => {
  if (!antenna) return false;

  return REQUIRED_ANTENNA_FIELDS.every((field) => {
    const value = antenna[field];
    return value !== undefined && value !== null && value !== "";
  });
};

export const onExtractRegisterData = (registersData) =>
  registersData.items.map((item: IItems) => ({
    id: item.id,
    locationDescription: item.locationDescription,
    readerNumber: item.readerNumber,
    readerName: item.readerName,
    readerIp: item.readerIp,
    configId: item.configId,
    modelDescription: item.modelDescription,
    port: item.port,
    protocol: item.protocol,
    status: item.status,
    antennas: item.antennas?.map((antennaItem: IDetailsItems) => ({
      readerLabel: antennaItem.readerLabel,
      antennaNumber: antennaItem.antennaNumber,
      antennaDescription: antennaItem.antennaDescription,
      sector: antennaItem.sector,
      flow: antennaItem.flow,
      antennaPort: antennaItem.antennaPort,
    })),
  }));

const onExtractReaderAntennaData = (register: IForm) => {
  const validAntennas = (register.antennas || []).filter(isAntennaComplete);

  return {
    id: register.reader.id ?? null,
    locationId: register.reader.locationId,
    readerNumber: register.reader.readerNumber,
    readerPositionType: register.reader.readerPositionType,
    readerIp: register.reader.readerIp,
    configId: register.reader.configId,
    modelType: register.reader.modelType,
    port: register.reader.port,
    protocol: register.reader.protocol,
    status: Boolean(register.reader.status),
    antennas: validAntennas.map((x: IAntennaForm) => ({
      id: x.id ?? null,
      readerLabel: x.modelDescription,
      antennaNumber: x.antennaNumber,
      antennaPort: x.antennaPort,
      antennaPositionType: x.antennaPositionType,
      flow: x.flow,
      model: x.model,
      sector: x.sector,
      serieNumber: x.serieNumber,
      status: Boolean(x.status),
    })),
  };
};

const ANTENNA_SLOTS = 4;

const normalizeAntennas = (backendAntennas = [], modelDescription = "") => {
  return Array.from({ length: ANTENNA_SLOTS }).map((_, index) => {
    const antenna = backendAntennas[index];

    return {
      id: antenna?.id,
      modelDescription,
      antennaNumber: antenna?.antennaNumber,
      antennaPort: antenna?.antennaPort,
      antennaPositionType: antenna?.antennaPositionType,
      flow: antenna?.flow,
      model: antenna?.model,
      sector: antenna?.sector,
      serieNumber: antenna?.serieNumber,
      status: Boolean(antenna?.status),
    };
  });
};

const onExtractReaderAntennaById = (register) => {
  const modelDescription = accessPointOptionsReader.find((item) => item.id === register.readerPositionType)?.description || "";

  return {
    reader: {
      id: register.id,
      locationId: register.locationId ?? null,
      readerNumber: register.readerNumber ?? null,
      readerPositionType: register.readerPositionType ?? null,
      readerIp: register.readerIp ?? null,
      configId: register.configId ?? null,
      modelType: register.modelType ?? null,
      port: register.port ?? null,
      protocol: register.protocol ?? null,
      status: Boolean(register.status),
    },
    antennas: normalizeAntennas(register.antennas, modelDescription),
  };
};

export function* onReadersRegisterFetchAsync() {
  try {
    yield put(CreatorsReaders.readersShowLoadingTable());
    const readers = yield select(getReadersFromState);
    const resp = yield call(Api.getReadersRegister, {
      filter: readers.searchFilter,
      _size: readers.filter.itemsByPage,
      _page: readers.filter.pageIndex,
    });
    const readersData = resp.data;
    const readersItems = onExtractRegisterData(readersData);
    yield put(CreatorsReaders.readersFetchRegister(readersItems, readersData.totalItems));
  } catch (error) {
    const requestErrorMessage = yield getError(error) ?? translate("readers_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(CreatorsReaders.readersHideLoadingTable());
  }
}

export function* onReadersAntennaUpdatedAsync() {
  try {
    yield put(CreatorsReaders.readersShowLoadingDrawer());
    const readersAntennas = yield select(getReadersFromState);

    const antennas: IAntennaForm[] = readersAntennas.form.antennas || [];
    const readerId = readersAntennas.form?.reader?.id;
    const hasAtLeastOneValidAntenna = antennas.some(isAntennaComplete);

    const warningMessage = yield translate("at_least_one_valid_antenna_must_be_registered");

    if (!hasAtLeastOneValidAntenna) {
      yield YmsNotification({
        type: "error",
        description: warningMessage,
      });
      return;
    }
    const formData = onExtractReaderAntennaData(readersAntennas.form);

    if (readerId) {
      yield call(Api.updateReaderAntennasRegister as any, formData);

      yield put(CreatorsReaders.readersHideDrawer());
      yield YmsNotification({
        type: "success",
        description: yield translate("reader_successfully_updated"),
      });
    } else {
      yield call(Api.createReaderAntennasRegister as any, formData);

      yield put(CreatorsReaders.readersHideDrawer());
      yield YmsNotification({
        type: "success",
        description: yield translate("reader_successfully_registered"),
      });
    }
    yield onReadersRegisterFetchAsync();
  } catch (error) {
    const requestErrorMessage = yield getError(error) ?? translate("reader_could_not_be_saved");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(CreatorsReaders.readersHideLoadingDrawer());
  }
}

export function* onReaderAntennasFetchByIdAsync(action) {
  try {
    yield put(CreatorsReaders.readersShowLoadingForm());

    const resp = yield call(Api.getReaderAntennasById, { id: action.id });
    const data = onExtractReaderAntennaById(resp.data);
    yield put(CreatorsReaders.readersUpdateFormReaderAntenna(data));
  } catch (error) {
    yield put(CreatorsReaders.readersHideLoadingDrawer());
    yield put(CreatorsReaders.readersHideDrawer());
    const requestErrorMessage = yield getError(error) ?? translate("tag_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(CreatorsReaders.readersHideLoadingForm());
  }
}

export function* onReaderDeleteByIdAsync(action) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(CreatorsReaders.readersShowLoading());
    yield call(Api.deleteReader, action.readerId);
    const registrationSuccessfullyMessage = yield translate("reader_removed_with_successfully");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield put(CreatorsReaders.readersHideDeleteModal());
    yield onReadersRegisterFetchAsync();
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("could_not_remove_reader");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(CreatorsReaders.readersHideLoading());
  }
}

export default function* ReadersSagas() {
  yield takeLatest(Types.READERS_REGISTER_FETCH_ASYNC, onReadersRegisterFetchAsync);
  yield takeLatest(Types.READERS_ANTENNA_UPDATED_ASYNC, onReadersAntennaUpdatedAsync);
  yield takeLatest(Types.READERS_ANTENNA_FETCH_BY_ID_ASYNC, onReaderAntennasFetchByIdAsync);
  yield takeLatest(Types.READERS_ANTENNA_READER_DELETE_BY_ID_ASYNC, onReaderDeleteByIdAsync);
}
